import { Link } from "react-router-dom";
import BrainLogo from "@/components/BrainLogo";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import LegalLinks from "@/components/LegalLinks";

export const UPDATED = "October 24, 2026";

/**
 * Generic legal page chrome — used by Terms, Privacy, and any future legal pages.
 * Keeps the same dark aesthetic as the rest of the app with a calm, readable
 * single-column body and clearly numbered sections.
 */
export function LegalShell({ eyebrow, title, updated, children, testid, seo }) {
  return (
    <div className="min-h-screen bg-[#0A0A0B] text-[#F4F4F5]" data-testid={testid}>
      {seo && <Seo title={seo.title} description={seo.description} path={seo.path} />}
      <header className="border-b border-white/5 px-5 md:px-8 py-4">
        <div className="max-w-[820px] mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="legal-home-link">
            <BrainLogo size={26} />
            <span className="font-semibold tracking-tight">Studivault</span>
          </Link>
          <Link to="/" className="text-[12.5px] text-[#B0AEBC] hover:text-white flex items-center gap-1">
            <ArrowLeft size={13} /> Home
          </Link>
        </div>
      </header>

      <motion.main
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="max-w-[760px] mx-auto px-5 md:px-8 py-14 md:py-20"
      >
        <div className="eyebrow mb-3">{eyebrow}</div>
        <h1 className="display-2 mb-3">{title}</h1>
        <div className="text-[12.5px] text-[#8E8B99] mb-10">Last updated · {updated}</div>

        <div className="prose prose-invert max-w-none legal-prose">
          {children}
        </div>
      </motion.main>

      <footer className="border-t border-white/5 px-5 md:px-8 py-8 mt-10">
        <div className="max-w-[820px] mx-auto flex flex-col md:flex-row items-center justify-between gap-2 text-[12px] text-[#8E8B99]">
          <span>© 2026 Studivault · Bloc Metric, India</span>
          <LegalLinks />
        </div>
      </footer>
    </div>
  );
}

export function Section({ n, title, children }) {
  return (
    <section className="mb-9">
      <h2 className="text-[18px] font-semibold text-[#F4F4F5] mb-3 flex items-baseline gap-3">
        <span className="text-[#7C5CFF] text-[14px] font-mono">{String(n).padStart(2, "0")}</span>
        {title}
      </h2>
      <div className="text-[14.5px] text-[#B0AEBC] leading-[1.7] space-y-3">{children}</div>
    </section>
  );
}
